import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Chip,
  Skeleton,
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { RefreshCw, AlertTriangle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const LOW_STOCK_THRESHOLD = 10;

const getLatestQty = (product) => {
  if (!product?.inventoryLevels || product.inventoryLevels.length === 0) return null;
  const last = product.inventoryLevels[product.inventoryLevels.length - 1];
  return Number(last.quantity ?? last.qty ?? 0);
};

const StockBadge = ({ qty }) => {
  if (qty === null || qty === undefined) return null;
  if (qty === 0) {
    return (
      <Chip
        size="small"
        icon={<XCircle size={11} />}
        label="Out"
        sx={{
          height: 20, fontSize: '0.65rem', fontWeight: 600,
          bgcolor: 'rgba(239,68,68,0.10)', color: '#ef4444',
          border: '1px solid rgba(239,68,68,0.25)',
          '& .MuiChip-icon': { color: '#ef4444' },
        }}
      />
    );
  }
  if (qty <= LOW_STOCK_THRESHOLD) {
    return (
      <Chip
        size="small"
        icon={<AlertTriangle size={11} />}
        label="Low"
        sx={{
          height: 20, fontSize: '0.65rem', fontWeight: 600,
          bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b',
          border: '1px solid rgba(245,158,11,0.25)',
          '& .MuiChip-icon': { color: '#f59e0b' },
        }}
      />
    );
  }
  return null;
};

function ProductList() {
  const [products, setProducts] = useState([]);
  const [nonZeroProducts, setNonZeroProducts] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    axios
      .get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/products-with-data')
      .then((response) => {
        const products = response.data || [];
        const nonZero = products.filter(
          (product) =>
            product.inventoryLevels &&
            product.inventoryLevels.length > 0 &&
            product.inventoryLevels[product.inventoryLevels.length - 1].quantity > 0
        );
        console.log(products);
        setNonZeroProducts(nonZero);
        setProducts(products);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        setLoading(false);
      });
  };

  const fetchTopSellingProducts = () => {
    axios
      .get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/top-selling-products')
      .then((response) => {
        const products = response.data || [];
        setTopSellingProducts(products);
        if (products.length > 0 && products[0].lastUpdated) {
          setLastUpdated(products[0].lastUpdated);
        }
      })
      .catch((error) => {
        console.error('Error fetching top selling products:', error);
      });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found! Redirecting to login...");
      navigate("/login");
      return;
    }
    console.log("Using Token:", token);
    axios.get("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile", {
      headers: { "Authorization": `Bearer ${token}` },
      withCredentials: true,
    })
      .then(res => console.log("User authenticated:", res.data))
      .catch(error => {
        console.error("Authentication failed:", error.response?.data);
        navigate("/login");
      });
    fetchData();
    fetchTopSellingProducts();
  }, [navigate]);

  const handleTabChange = (event, newValue) => setSelectedTab(newValue);

  const handleUpdateAllClick = () => {
    setUpdating(true);
    const t = toast.loading('Updating total orders…');
    axios
      .get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/recalculate-total-orders')
      .then((response) => {
        console.log('Total orders updated successfully for all products:', response.data);
        fetchTopSellingProducts();
        toast.success('Total orders updated', { id: t });
      })
      .catch((error) => {
        console.error('Error updating total orders for all products:', error);
        toast.error('Failed to update total orders', { id: t });
      })
      .finally(() => setUpdating(false));
  };

  const getDisplayedProducts = () => {
    if (selectedTab === 0) return nonZeroProducts;
    if (selectedTab === 2) return topSellingProducts;
    return products;
  };

  const formatLastUpdated = (dateString) => {
    const options = {
      timeZone: 'America/Chicago',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    };
    return new Date(dateString).toLocaleString('en-US', options);
  };

  return (
    <Box className="fade-in">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
            Products
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Overview of all product inventory
          </Typography>
        </Box>
        {selectedTab === 2 && (
          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleUpdateAllClick}
              disabled={updating}
              startIcon={<RefreshCw size={14} className={updating ? 'spinning' : ''} />}
            >
              {updating ? 'Updating...' : 'Update Orders'}
            </Button>
            {lastUpdated && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Updated: {formatLastUpdated(lastUpdated)}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab label="In Stock" />
          <Tab label="All Products" />
          <Tab label="Top Selling" />
        </Tabs>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: selectedTab === 2 ? '1fr 140px' : '1fr 140px 160px',
          px: 2, py: 1.25,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', fontSize: '0.7rem' }}>
            Product
          </Typography>
          {selectedTab !== 2 && (
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', textAlign: 'right', fontSize: '0.7rem' }}>
              Last Updated
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', textAlign: 'right', fontSize: '0.7rem' }}>
            {selectedTab === 2 ? 'Total Orders' : 'Inventory'}
          </Typography>
        </Box>

        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <Box
              key={`sk-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: selectedTab === 2 ? '1fr 140px' : '1fr 140px 160px',
                px: 2, py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                alignItems: 'center',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Skeleton variant="text" width="55%" />
              {selectedTab !== 2 && <Skeleton variant="text" width="50%" sx={{ ml: 'auto' }} />}
              <Skeleton variant="text" width="60%" sx={{ ml: 'auto' }} />
            </Box>
          ))
        ) : getDisplayedProducts().length > 0 ? (
          getDisplayedProducts().map((product) => {
            const qty = getLatestQty(product);
            return (
              <Box
                key={product.shopifyId || product.productId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: selectedTab === 2 ? '1fr 140px' : '1fr 140px 160px',
                  px: 2, py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  alignItems: 'center',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {product.title}
                </Typography>
                {selectedTab !== 2 && (
                  <Typography variant="body2" sx={{ textAlign: 'right', color: 'text.secondary' }}>
                    {product.inventoryLevels && product.inventoryLevels.length > 0
                      ? product.inventoryLevels[product.inventoryLevels.length - 1].date.split('T')[0]
                      : 'No Data'}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                  {selectedTab !== 2 && <StockBadge qty={qty} />}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedTab === 2
                      ? (product.totalOrders !== undefined ? product.totalOrders : 'N/A')
                      : (qty !== null ? qty : 'N/A')}
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedTab === 0 ? 'No products with inventory.' : selectedTab === 2 ? 'No top selling data.' : 'No products found.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ProductList;
